import { useMemo, useState, useCallback } from 'react';
import {
  Text, Paper, Stack, Group, Table, Skeleton, Drawer,
  SimpleGrid, Divider, ActionIcon, Tooltip, Pagination,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronUp, IconChevronDown, IconSelector, IconEye, IconReceipt,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useMarketData } from '@/hooks/usePortalApi';

type SortField = 'commodity';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

export default function MarketPrices() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [drawerItem, setDrawerItem] = useState<any>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const { data, isLoading } = useMarketData();
  const allItems = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allItems;
    return [...allItems].sort((a, b) => {
      const cmp = String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allItems, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const items = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleView = useCallback((item: any) => { setDrawerItem(item); openDrawer(); }, [openDrawer]);

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader title="Market Prices" description="Monitor commodity market prices and trends" />
      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : items.length === 0 ? (
        <EmptyState icon={<IconReceipt size={30} />} title="No market data" description="No market price data found" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>ID</Table.Th>
                  <ThSort field="commodity">Commodity</ThSort>
                  <Table.Th>Market</Table.Th>
                  <Table.Th>Price</Table.Th>
                  <Table.Th>Change %</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item: any) => (
                  <Table.Tr key={item.id}>
                    <Table.Td><Text size="sm" style={{ fontFamily: 'monospace' }}>{item.id}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{item.commodity}</Text></Table.Td>
                    <Table.Td><Text size="sm">{item.market}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{formatCurrency(item.price ?? 0)}</Text></Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" c={item.change_pct >= 0 ? 'green' : 'red'} fw={500}>
                        {item.change_pct >= 0 ? '+' : ''}{(item.change_pct ?? 0).toFixed(2)}%
                      </Text>
                    </Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{item.date}</Text></Table.Td>
                    <Table.Td>
                      <Tooltip label="View"><ActionIcon variant="subtle" size="sm" onClick={() => handleView(item)}><IconEye size={16} /></ActionIcon></Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}
      <Drawer opened={drawerOpen} onClose={closeDrawer} position="right" size="lg" title={<Text fw={700} size="lg">Market Price Details</Text>}>
        {drawerItem && (
          <Stack gap="md">
            <Divider />
            <SimpleGrid cols={2}>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>ID</Text><Text size="sm" fw={500}>{drawerItem.id}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Commodity</Text><Text size="sm" fw={500}>{drawerItem.commodity}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Market</Text><Text size="sm" fw={500}>{drawerItem.market}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Price</Text><Text size="sm" fw={500}>{formatCurrency(drawerItem.price ?? 0)}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Change %</Text><Text size="sm" fw={500} c={drawerItem.change_pct >= 0 ? 'green' : 'red'}>{drawerItem.change_pct >= 0 ? '+' : ''}{(drawerItem.change_pct ?? 0).toFixed(2)}%</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Date</Text><Text size="sm" fw={500}>{drawerItem.date}</Text></div>
            </SimpleGrid>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
